import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { IoArrowBackSharp } from "react-icons/io5";
import logo from "../../Images/logo-4.png";
import { BASEAPIURL, token } from "../../env";
import PropTypes from "prop-types";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import CryptoJS from "crypto-js";
import { formatDate } from "../commonFunctionlity";

const AccountStatement = ({ showBackButton = false }) => {
  const [accountData, setAccountData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [memberData, setMemberData] = useState({});
  const [memberImage, setMemberImage] = useState(logo);

  const navigate = useNavigate();
  const memberID = sessionStorage.getItem("MemberID");
  const memberName = sessionStorage.getItem("MemberName");

  useEffect(() => {
    if (!memberID) {
      setError("No MemberID found in sessionStorage.");
      setLoading(false);
      return;
    }

    const myHeaders = new Headers({ "Content-Type": "application/json" });
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({ tokenData: token }),
      redirect: "follow",
    };

    fetch(
      `${BASEAPIURL}/MemberByID?MainID=${memberID}`,
      requestOptions
    )
      .then((res) => {
        if (!res.ok)
          throw new Error(`Error fetching member data: ${res.status}`);
        return res.json();
      })
      .then((result) => {
        if (!result?.data) throw new Error("No member data found.");
        const data = result.data;
        setMemberData(data);
        if (data?.ImageBase64?.startsWith("data:image")) {
          setMemberImage(data.ImageBase64);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to fetch member data.");
        setLoading(false);
      });
  }, [memberID]);

  useEffect(() => {
    const fetchAccountData = async () => {
      setLoading(true);
      setError(null);

      if (!memberID) {
        setError("MemberID is missing. Please log in again.");
        setLoading(false);
        return;
      }

      const headers = new Headers({ "Content-Type": "application/json" });
      const tokenData = { tokenData: token };

      try {
        const response = await fetch(
          `${BASEAPIURL}/StatementOfAcListByMemID?MemID=${memberID}`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(tokenData),
          }
        );

        const resultText = await response.text();
        let parsedResult = null;

        try {
          parsedResult = resultText ? JSON.parse(resultText) : null;
        } catch (e) {
          console.warn("Failed to parse response JSON:", e);
        }

        if (
          !response.ok ||
          !parsedResult?.status ||
          !Array.isArray(parsedResult?.data)
        ) {
          setAccountData([]);
          return;
        }

        const sortedData = parsedResult.data.sort(
          (a, b) => new Date(b.VouDate) - new Date(a.VouDate)
        );
        setAccountData(sortedData);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Unable to load account data.");
        setAccountData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAccountData();
  }, [memberID]);

  const handleBack = () => navigate(-1);


  const formatCurrency = (amount) => `₹${(Number(amount) || 0).toFixed(2)}`;

  const { totalCredit, totalDebit, netBalance } = accountData.reduce(
    (totals, transaction) => {
      const credit = Number(transaction.CreditAmount) || 0;
      const debit = Number(transaction.DebitAmount) || 0;
      const net = debit - credit;

      return {
        totalCredit: totals.totalCredit + credit,
        totalDebit: totals.totalDebit + debit,
        netBalance: totals.netBalance + net,
      };
    },
    { totalCredit: 0, totalDebit: 0, netBalance: 0 }
  );

  const adjustedDebit = netBalance > 0 ? totalDebit + netBalance : totalDebit;
  const adjustedCredit =
    netBalance < 0 ? totalCredit + Math.abs(netBalance) : totalCredit;

  const encrypt = (plainText, key) => {
    try {
      const encrypted = CryptoJS.AES.encrypt(
        CryptoJS.enc.Utf8.parse(plainText),
        CryptoJS.enc.Utf8.parse(key),
        {
          mode: CryptoJS.mode.ECB,
          padding: CryptoJS.pad.Pkcs7,
        }
      );
      return CryptoJS.enc.Base64.stringify(encrypted.ciphertext);
    } catch (err) {
      console.error("Encryption error:", err);
      alert("Failed to encrypt payment data.");
      return null;
    }
  };

  const handlePayBill = async () => {
    if (netBalance === 0) {
      alert("No outstanding credit amount to pay.");
      return;
    }

    setLoading(true);
    try {
      const payments = {
        OrderType: "SOA",
        OrderMemberID: memberID || 0,
        OrderDate: new Date().toISOString(),
        OrderMainID: memberID || 0,
        PaidAmount: Math.abs(netBalance) || 0,
        RequestAmount: Math.abs(netBalance) || 0,
      };

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        tokenData: token,
        Payment: payments,
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch(
        `${BASEAPIURL}/InsertPaymentOrder`,
        requestOptions
      );

      const result = await response.text();
      console.log("InsertPaymentOrder Response:", result);

      if (response.ok) {
        let responseObject;
        try {
          responseObject = JSON.parse(result);
        } catch (e) {
          const match = result.match(/"OrderID"\s*:\s*"([^"]+)"/);
          if (match) {
            responseObject = { OrderID: match[1] };
          } else {
            throw new Error("Could not parse OrderID from response");
          }
        }
        const finalOrderID = responseObject?.OrderID;

        if (finalOrderID) {
          alert(`Redirecting to payment gateway. Order ID: ${finalOrderID}`);
          // const merchantId = "142716";
          const merchantId = "127787";
          const subMerchantId = "11";
          const paymode = "9";
          const referenceNo = `${finalOrderID}`;
          const transactionAmount = Math.abs(netBalance).toFixed(2);
          const mandatoryfields = `${referenceNo}|${subMerchantId}|${Math.abs(
            netBalance
          ).toFixed(2)}`;
          const optionalFields = `${memberData.MemName}|${memberData.MemMSNO}|${0}`;
          const returnurl = `https://jaiclub.in/PaymentIntegration`;

          // const encryptionKey = "1400010227105020";
          const encryptionKey = "1267642077805600";

          const encMandatoryFields = encrypt(mandatoryfields, encryptionKey);
          const encOptional = encrypt(optionalFields, encryptionKey);
          const encReturnUrl = encrypt(returnurl, encryptionKey);
          const encReferenceNo = encrypt(referenceNo, encryptionKey);
          const encSubMerchantId = encrypt(subMerchantId, encryptionKey);
          const encPGAmount = encrypt(transactionAmount, encryptionKey);
          const encPaymode = encrypt(paymode, encryptionKey);

          if (
            !encMandatoryFields ||
            !encReferenceNo ||
            !encSubMerchantId ||
            !encPGAmount ||
            !encReturnUrl
          ) {
            alert("❌ Encryption failed. Cannot proceed to payment.");
            setLoading(false);
            return;
          }

          const baseUrl = "https://eazypay.icicibank.com/EazyPG";

          const params =
            `merchantid=${merchantId}` +
            `&mandatory fields=${encodeURIComponent(encMandatoryFields)}` +
            `&optional fields=${encOptional}` +
            `&returnurl=${encReturnUrl}` +
            `&Reference No=${encReferenceNo}` +
            `&submerchantid=${encSubMerchantId}` +
            `&transaction amount=${encPGAmount}` +
            `&paymode=${encPaymode}`;
          const plainparams =
            `merchantid=${merchantId}` +
            `&mandatory fields=${mandatoryfields}` +
            `&optional fields=${optionalFields}` +
            `&returnurl=${returnurl}` +
            `&Reference No=${referenceNo}` +
            `&submerchantid=${subMerchantId}` +
            `&transaction amount=${transactionAmount}` +
            `&paymode=${paymode}`;

          const encryptedUrl = `${baseUrl}?${params}`;
          const plainurl = `${baseUrl}?${plainparams}`;

          console.log("🔒 Encrypted Eazypay URL (GET):", encryptedUrl);
          console.log("Plain Eazypay URL (GET):", plainurl);

          window.location.href = encryptedUrl;
        } else {
          alert("✅ Payment request sent, but OrderID not found in response.");
        }
      } else {
        alert("❌ Payment request failed.");
      }
    } catch (error) {
      console.error("Payment Error:", error);
      alert("⚠️ An error occurred during payment initiation.");
    } finally {
      setLoading(false);
    }
  };
  const exportToExcel = () => {
    const worksheetData = accountData.map((row) => ({
      "Voucher No": row.VNo || "N/A",
      Date: formatDate(row.VouDate),
      Description: row.VDesc || "No description",
      Location: row.VType?.trim() || "N/A",
      "Bill Amount": Number(row.VAmount) || 0,
      Debit: Number(row.DebitAmount) || 0,
      Credit: Number(row.CreditAmount) || 0,
    }));

    worksheetData.push({});
    worksheetData.push({
      Description: "Sub Total",
      Debit: totalDebit,
      Credit: totalCredit,
    });
    worksheetData.push({
      Description: "Balance",
      Debit: netBalance > 0 ? netBalance : 0,
      Credit: netBalance < 0 ? Math.abs(netBalance) : 0,
    });
    worksheetData.push({
      Description: "Total",
      Debit: adjustedDebit,
      Credit: adjustedCredit,
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Account Statement");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const fileData = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(fileData, `AccountStatement_${memberName || "Member"}.xlsx`);
  };

  const exportStatementToPdf = () => {
    const doc = new jsPDF({
      orientation: "landscape",
    });

    var xOffset = doc.internal.pageSize.width / 2;
    doc.setFontSize(12);
    doc.text("Statement of Account", xOffset, 8, { align: "center" });
    doc.setFontSize(10);
    doc.text(
      `${memberData.MemName || "member"} (${memberData.MemMSNO})`,
      xOffset,
      12,
      { align: "center" }
    );

    const tableData = accountData?.map((item) => {
      return [
        `${item?.VNo}`,
        `${item?.VouDate}`,
        `${item?.VDesc}`,
        ``,
        `${item?.VAmount}`,
        `${item?.DebitAmount}`,
        `${item?.CreditAmount}`,
      ];
    });

    const tableHeaders = [
      "Voucher No",
      "Date",
      "Description",
      "Location",
      "Bill Amount",
      "Debit",
      "Credit",
    ];

    const tableFooterSubtotal = [
      "Sub Total",
      "",
      "",
      "",
      "",
      `${totalDebit} Dr`,
      `${totalCredit} Cr`,
    ];

    const tableFooterBalance = [
      "Balance",
      "",
      "",
      "",
      "",
      `${netBalance > 0 ? netBalance : "0"} Dr`,
      `${netBalance < 0 ? Math.abs(netBalance) : "0"} Cr`,
    ];

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      foot: [tableFooterSubtotal, tableFooterBalance],
      showHead: "firstPage",
      showFoot: "lastPage",
      theme: "striped",
      columnStyles: { text: { cellWidth: "auto" } },
      margin: { left: 3, right: 3 },
      styles: {
        overflow: "linebreak",
        cellWidth: "wrap",
        cellPadding: 1,
        fontSize: 6,
      },
      autoWidth: true,
    });

    doc.save(`Account_Statement_${memberData.MemName || "member"}.pdf`);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-secondary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {showBackButton && (
        <>
          <div className="mb-4">
            <button
              className="btn btn-outline-secondary d-flex align-items-center"
              onClick={handleBack}
            >
              <IoArrowBackSharp className="me-2" />
              Back
            </button>
          </div>
          <div
            className="rounded-5"
            style={{
              minHeight: "50px",
              padding: "10px",
              marginTop: "50px",
              background:
                "linear-gradient(90deg, rgba(200, 182, 166, 1) 17%, rgba(217, 164, 117, 1) 66%, rgba(252, 176, 69, 1) 100%)",
            }}
          >
            <div className="row align-items-center">
              <div className="col-12 col-md-4 text-center">
                <img
                  src={memberImage}
                  className="rounded-circle border border-4"
                  style={{
                    width: "150px",
                    height: "150px",
                    objectFit: "cover",
                    marginTop: "-70px",
                    backgroundColor: "white",
                  }}
                  alt="member"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = logo;
                  }}
                />
              </div>
              <div className="col-12 col-md-4 text-center text-white mt-3">
                <h3>{memberData.MemName || "N/A"}</h3>
              </div>
              <div className="col-12 col-md-4 text-center text-white mt-3">
                <h5>Member since: {formatDate(memberData.MemRegDt)}</h5>
                <h5>Membership Type: {memberData.MemType}</h5>
                <h5 className="mt-2">Membership No: {memberData.MemMSNO}</h5>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="card shadow">
        <div className="card-body">
          <h1 className="text-center mb-4 fw-bold text-dark">
            Account Statement
          </h1>
          <h5 className="text-center">{memberName || "N/A"}</h5>

          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-success text-white text-center">
                <tr>
                  <th>Voucher No</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Location</th>
                  <th>Bill Amount</th>
                  <th className="text-danger">Debit</th>
                  <th className="text-success">Credit</th>
                </tr>
              </thead>
              <tbody>
                {accountData.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted">
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  accountData.map((transaction, index) => (
                    <tr key={index}>
                      <td className="text-center">
                        {transaction.VNo || "N/A"}
                      </td>
                      <td className="text-center">
                        {formatDate(transaction.VouDate)}
                      </td>
                      <td className="text-center">
                        {transaction.VDesc || "No description"}
                      </td>
                      <td className="text-center">
                        {transaction.VType?.trim() || "N/A"}
                      </td>
                      <td className="text-center fw-bold">
                        {formatCurrency(transaction.VAmount)}
                      </td>
                      <td className="text-center text-danger">
                        {formatCurrency(transaction.DebitAmount)}
                      </td>
                      <td className="text-center text-success">
                        {formatCurrency(transaction.CreditAmount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="5" className="text-center fw-bold">
                    Sub Total
                  </td>
                  <td className="text-center text-danger">
                    {formatCurrency(totalDebit)} Dr
                  </td>
                  <td className="text-center text-success">
                    {formatCurrency(totalCredit)} Cr
                  </td>
                </tr>
                <tr>
                  <td colSpan="5" className="text-center fw-bold">
                    Balance
                  </td>
                  <td
                    className={`text-center ${
                      netBalance > 0 ? "text-danger" : ""
                    }`}
                  >
                    {netBalance > 0 ? formatCurrency(netBalance) : "0"} Dr
                  </td>
                  <td
                    className={`text-center ${
                      netBalance < 0 ? "text-success" : ""
                    }`}
                  >
                    {netBalance < 0
                      ? formatCurrency(Math.abs(netBalance))
                      : "0"}{" "}
                    Cr
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {error && (
            <div className="alert alert-danger text-center mt-3" role="alert">
              {error}
            </div>
          )}

          <div className="text-center mt-4">
            <button
              onClick={handlePayBill}
              className="btn btn-primary btn-lg mt-3"
            >
              Pay Bill ({formatCurrency(Math.abs(netBalance))})
            </button>
            <button
              onClick={exportToExcel}
              className="btn btn-success btn-lg mt-3 ms-3"
            >
              Export to Excel
            </button>
            <button
              onClick={exportStatementToPdf}
              className="btn btn-success btn-lg mt-3 ms-3"
            >
              Export to PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

AccountStatement.propTypes = {
  showBackButton: PropTypes.bool,
};

export default AccountStatement;
